import { z } from 'zod';

import type {
  PreregisterFixedIdAssetRequest,
  PreregisterFixedIdAssetResponse,
  StartFixedIdAssetUseRequest,
  StartFixedIdAssetUseResponse,
  UpdateFixedIdAssetMasterTareRequest,
  UpdateFixedIdAssetMasterTareResponse,
} from './fixed-id-assets-commands.dto.js';
import { FixedIdAssetSnapshotSchema } from './fixed-id-assets.schema.js';
import { HoldingUnitSnapshotSchema } from './holding-units.schema.js';

const timestampSchema = z.iso.datetime();

const commandContextBaseSchema = z.object({
  companyId: z.uuid(),
  storeId: z.uuid(),
  locationId: z.uuid(),
  stationId: z.uuid(),
  occurredAt: timestampSchema.optional(),
});

const commandContextSchema = commandContextBaseSchema.extend({
  expectedVersion: z.number().int().nonnegative(),
});

const fixedIdPhysicalIdentifierSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .superRefine((value, ctx) => {
    if (/[A-Za-z0-9]/.test(value)) {
      return;
    }
    ctx.addIssue({
      code: 'custom',
      message: 'physicalId must include at least one alphanumeric character.',
      path: [],
    });
  });

export const PreregisterFixedIdAssetRequestSchema: z.ZodType<PreregisterFixedIdAssetRequest> =
  commandContextSchema
    .extend({
      unitProfileId: z.uuid(),
      physicalId: fixedIdPhysicalIdentifierSchema,
      masterTareLb: z.number().nonnegative(),
      yardLocationId: z.uuid().nullable().optional(),
    })
    .superRefine((value, ctx) => {
      if (value.expectedVersion !== 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'expectedVersion must be 0 when preregistering a fixed-id asset.',
          path: ['expectedVersion'],
        });
      }
    });

export const PreregisterFixedIdAssetResponseSchema: z.ZodType<PreregisterFixedIdAssetResponse> =
  z.object({
    asset: FixedIdAssetSnapshotSchema,
  });

export const StartFixedIdAssetUseRequestSchema: z.ZodType<StartFixedIdAssetUseRequest> =
  commandContextBaseSchema.extend({
    assetId: z.uuid(),
    unitProfileId: z.uuid(),
    expectedAssetVersion: z.number().int().nonnegative(),
    yardLocationId: z.uuid().nullable().optional(),
  });

export const StartFixedIdAssetUseResponseSchema: z.ZodType<StartFixedIdAssetUseResponse> = z.object(
  {
    asset: FixedIdAssetSnapshotSchema,
    unit: HoldingUnitSnapshotSchema,
  },
);

export const UpdateFixedIdAssetMasterTareRequestSchema: z.ZodType<UpdateFixedIdAssetMasterTareRequest> =
  commandContextSchema.extend({
    assetId: z.uuid(),
    masterTareLb: z.number().nonnegative(),
  });

export const UpdateFixedIdAssetMasterTareResponseSchema: z.ZodType<UpdateFixedIdAssetMasterTareResponse> =
  z.object({
    asset: FixedIdAssetSnapshotSchema,
  });
